<script setup>
import { ref, computed, onMounted, getCurrentInstance, provide } from 'vue';
import { useStore } from 'vuex'
import _ from 'lodash'
import { sortStreams } from '@/utils'
import { usePersistedSort, DATE_NAME_SORT } from '@/Utils/usePersistedSort'
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue';
import AddMediaflowModal from "@/components/modals/AddMediaflowModal.vue"
import MediaflowItem from "@/components/Mediaflow/MediaflowItem.vue"
import MediaflowEmptyPlaceholder from "@/components/Mediaflow/MediaflowEmptyPlaceholder.vue"
import { useRouter } from 'vue-router'
import IconBase from '../../components/icon/IconBase.vue'
import IconPlus from '../../components/icon/IconPlus.vue'
import IconChevronUpDown from '../../components/icon/IconChevronUpDown.vue'
import IconCheckmark from '../../components/icon/IconCheckmark.vue'
import FormButton from '../../components/Atoms/FormButton.vue'
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue'
import ViewModeSwitcher from '../../components/ui/ViewModeSwitcher.vue';
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue';
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue';
import { useNotification } from "@kyvg/vue3-notification"
import Tooltip from '@/components/Atoms/Tooltip.vue';
import PageHeader from '@/components/ui/PageHeader.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import IconLock from '../../components/icon/IconLock.vue'
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue';
import ListingSearchEmpty from '@/components/ui/ListingSearchEmpty.vue';

provide('modalPlainBackdrop', true)

const store = useStore()
const router = useRouter()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const { notify } = useNotification()

const showMediaflowModal = ref()

const { sortBy, setSort } = usePersistedSort('castr.sort.mediaflow', DATE_NAME_SORT, 'Newest')
const searchWidgetInput = ref('')
const sortDropdown = ref()
const selectedMediaflowId = ref(null)

const viewMode = ref('grid')
const modalDEMOVideo = ref(null);

const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const isLiveSubscriptionExpired = computed(() => store.getters['User/isLiveSubscriptionExpired'])
const maxMediaflows = computed(() => store.getters['User/maxMediaflows'])
const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])
const mediaflows = computed(() => store.getters['Mediaflows/mediaflows'])
const showListControls = computed(() => (mediaflows.value?.length ?? 0) > 2)
const mediaflowTrial = computed(() => store.getters['User/mediaflowTrial'])
const sortByLabel = computed(() => sortBy.value)
const mediaflowFiltered = computed(() => {
	const q = searchWidgetInput.value.toLowerCase()
	const filtered = (mediaflows.value || []).filter(mf => mf.name?.toLowerCase().includes(q))
	const withCreationTime = filtered.map((mf) => ({
		...mf,
		creationTime: mf.creationTime || mf.createdAt || mf._id,
	}))
	return sortStreams(withCreationTime, sortBy.value) || withCreationTime
})

const mediaflowLimit = computed(() => {
	const c = maxMediaflows.value
	return typeof c === 'number' && c > 0 ? c : null
})

const mediaflowHeaderUsage = computed(() => {
	if (!mediaflows.value?.length) return null
	const meta = {
		used: mediaflows.value.length,
		label: 'productions',
		labelSingular: 'production',
	}
	if (mediaflowLimit.value) {
		meta.limit = mediaflowLimit.value
		meta.planLabel = 'cloud productions'
	}
	return meta
})

const atMediaflowLimit = computed(() => {
	const max = mediaflowLimit.value
	if (!max) return false
	return (mediaflows.value?.length ?? 0) >= max
})

onMounted(async () => {
	if (!store.getters['Mediaflows/isLoaded']) {
		store.dispatch('Ui/setLoadingStatus', true)
	} else {
		store.dispatch('Ui/setLoadingStatus', false)
	}
	try {
	  	await store.dispatch('Streams/getStreams')
		await store.dispatch('Mediaflows/getMediaflows')
		store.dispatch('Mediaflows/setTeamPermissionError', { error: false })
	} catch(err){
		if (err && err.toString().includes('not have access to')) {
			store.dispatch('Mediaflows/setTeamPermissionError', { error: true })
		}
		
		notify({
			group: "error",
			text: err || "Cloud Production load failed",
		});
	}
	store.dispatch('Ui/setLoadingStatus', false)
})

const onNewMF = async (player) => {
	await store.dispatch('Mediaflows/getMediaflows', true)
	router.push({
		name: "MediaflowContent",
		params: {
			id: player._id
		},
		query: {'sourceTab' : 'livestream'}
	})
}

const viewModeClasses = computed(() => (
	shouldShowCompactModeStreams.value
		? '!grid-cols-1 gap-1.5 auto-rows-fr'
		: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 auto-rows-fr gap-4'
))
const changeSortBy = (option) => {
	setSort(option)
	sortDropdown.value?.toggleDropdown()
}
const showCreateMediaflowModal = () => {
	showMediaflowModal.value.toggleSidebar()
}
const showDemo = () => {
	modalDEMOVideo.value.show()
}

</script>

<template>
	<div class="container detail-shell px-4">
		<PageHeader
			title="Cloud Production"
			subtitle="Switch between flows and go live from Castr's servers — productions keep streaming after you close the browser."
			mobile-inline
			:usage="!loadingStatus ? mediaflowHeaderUsage : null"
		>
			<template v-if="!loadingStatus && !isLiveSubscriptionExpired && mediaflowTrial">
				<Tooltip v-if="atMediaflowLimit" wrap-trigger :content="'Please upgrade your subscription to add more cloud productions'">
					<FormButton class="gap-1" size="md" :disabled="true" @click="showCreateMediaflowModal">
						<icon-base class="mr-2">
							<icon-plus />
						</icon-base>
						New <span class="hidden md:block">Cloud Production</span>
					</FormButton>
				</Tooltip>
				<FormButton v-else class="gap-1" size="md" @click="showCreateMediaflowModal">
					<icon-base class="mr-2">
						<icon-plus />
					</icon-base>
					New <span class="hidden md:block">Cloud Production</span>
				</FormButton>
			</template>
		</PageHeader>
		<section v-if="loadingStatus" aria-busy="true" aria-label="Loading productions" class="mt-3">
			<ul class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
				<li v-for="n in 3" :key="n" class="flex flex-col overflow-hidden rounded-xl bg-surface-2 ring-1 ring-surface-3">
					<div class="aspect-video w-full animate-pulse bg-surface-3" />
					<div class="flex flex-col gap-2 p-3.5">
						<div class="h-4 w-2/3 animate-pulse rounded bg-surface-3" />
						<div class="h-3 w-1/2 animate-pulse rounded bg-surface-3" />
					</div>
				</li>
			</ul>
		</section>
		<main v-else-if="isLiveSubscriptionExpired">
			<EmptyState title="Upgrade to unlock Cloud Production" subtitle="Produce and switch between live sources in the cloud. It's not included in your current plan.">
				<template #icon>
					<icon-base width="28" height="28" fill="none">
						<icon-lock />
					</icon-base>
				</template>
				<template #actions>
					<FormButton to="/subscribe">
						Upgrade Plan
					</FormButton>
				</template>
			</EmptyState>
		</main>
		<main v-else-if="!mediaflowTrial">
			<EmptyState title="Cloud Production is restricted" subtitle="Produce and switch between live sources in the cloud. It's not available on your current plan.">
				<template #icon>
					<icon-base width="28" height="28" fill="none">
						<icon-lock />
					</icon-base>
				</template>
			</EmptyState>
		</main>
		<MediaflowEmptyPlaceholder v-else-if="mediaflows.length === 0"
			@createMediaflow="showCreateMediaflowModal" @showDemo="showDemo" />
		<main v-else>
			<ResponsiveToolbar class="my-3">
				<template v-if="showListControls">
				<DropDownMenu ref="sortDropdown" type="secondary" position="left">
					<template #toggle-button>
						<span class="text-surface-8">Sort by:&nbsp;</span>
						<span class="text-white">{{ sortByLabel }}</span>
						<icon-base class="!ml-1 shrink-0 text-surface-8">
							<icon-chevron-up-down />
						</icon-base>
					</template>
					<template #menu-items>
						<DropDownMenuItem :active="sortBy === 'Oldest'" @click="changeSortBy('Oldest')">
							Oldest
							<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Oldest'" fill="none">
								<icon-checkmark />
							</icon-base>
						</DropDownMenuItem>
						<DropDownMenuItem :active="sortBy === 'Newest'" @click="changeSortBy('Newest')">
							Newest
							<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Newest'" fill="none">
								<icon-checkmark />
							</icon-base>
						</DropDownMenuItem>
						<DropDownMenuItem :active="sortBy === 'A to Z'" @click="changeSortBy('A to Z')">
							A to Z
							<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'A to Z'" fill="none">
								<icon-checkmark />
							</icon-base>
						</DropDownMenuItem>
						<DropDownMenuItem :active="sortBy === 'Z to A'" @click="changeSortBy('Z to A')">
							Z to A
							<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Z to A'" fill="none">
								<icon-checkmark />
							</icon-base>
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
				<ListingSearchInput v-model="searchWidgetInput" />
				</template>
				<ViewModeSwitcher class="shrink-0" />
			</ResponsiveToolbar>
			<ListingSearchEmpty v-if="searchWidgetInput.trim() && !mediaflowFiltered.length" :query="searchWidgetInput" noun="productions" @clear="searchWidgetInput = ''" />
			<section v-else>
				<ul class="grid" :class="viewModeClasses">
					<li class="" v-for="mediaflow in mediaflowFiltered" :key="mediaflow._id">
						<mediaflow-item :mediaflow="mediaflow" :view-mode="viewMode" />
					</li>
					<li v-if="!searchWidgetInput">
						<ListingCreateTile
							label="Cloud Production"
							:view="shouldShowCompactModeStreams ? 'list' : 'thumbnail'"
							:used="mediaflows.length"
							:limit="mediaflowLimit"
							:limit-reached="atMediaflowLimit"
							@create="showCreateMediaflowModal"
						/>
					</li>
				</ul>
			</section>
		</main>
		<demo-video-modal ref="modalDEMOVideo" videoid="cmfns2a1v0wt71d3n7eh7i6bt" />
		<add-mediaflow-modal @mediaflow-added="onNewMF" ref="showMediaflowModal" />
	</div>
</template>

<style scoped>

</style> 